const REGION_CHORDS = [
  [82.41, 123.47, 164.81], // last light — hull-low
  [98, 146.83, 196], // distant signal — warm low
  [110, 164.81, 220], // approach — open
  [92.5, 138.59, 185], // eclipse — tense
  [123.47, 185, 246.94], // braids — glassy
  [116.54, 174.61, 233.08], // pale current — suspended
  [130.81, 196, 261.63], // far shore — open fifths
];

export function createAmbient() {
  let context;
  let master;
  let reverb;
  let filter;
  let noiseGain;
  let droneGain;
  let hullOsc;
  let hullGain;
  let running = false;
  let lastRegion = -1;
  const drones = [];

  function impulse(duration = 2.8, decay = 2.6) {
    const length = Math.floor(context.sampleRate * duration);
    const buffer = context.createBuffer(2, length, context.sampleRate);
    for (let channel = 0; channel < 2; channel += 1) {
      const data = buffer.getChannelData(channel);
      for (let i = 0; i < length; i += 1) {
        // Local procedural noise — not Math.random of game state
        data[i] = (Math.sin(i * 12.9898 + channel * 78.233) * 43758.5453 % 1) * 2 - 1;
        data[i] *= (1 - i / length) ** decay;
      }
    }
    return buffer;
  }

  function build() {
    context = new (window.AudioContext || window.webkitAudioContext)();
    master = context.createGain();
    master.gain.value = 0.14;
    master.connect(context.destination);

    reverb = context.createConvolver();
    reverb.buffer = impulse();
    const reverbGain = context.createGain();
    reverbGain.gain.value = 0.28;
    reverb.connect(reverbGain).connect(master);

    droneGain = context.createGain();
    droneGain.gain.value = 0.14;
    droneGain.connect(master);
    droneGain.connect(reverb);

    REGION_CHORDS[0].forEach((frequency, index) => {
      const oscillator = context.createOscillator();
      oscillator.type = index === 0 ? 'sine' : 'triangle';
      oscillator.frequency.value = frequency;
      const gain = context.createGain();
      gain.gain.value = index === 0 ? 0.14 : 0.03;
      oscillator.connect(gain).connect(droneGain);
      oscillator.start();
      drones.push({ oscillator, gain });
    });

    // Hull hum
    hullOsc = context.createOscillator();
    hullOsc.type = 'sine';
    hullOsc.frequency.value = 42;
    hullGain = context.createGain();
    hullGain.gain.value = 0.05;
    hullOsc.connect(hullGain).connect(master);
    hullOsc.start();

    // Soft canopy air
    const buffer = context.createBuffer(1, context.sampleRate * 3, context.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i += 1) {
      data[i] = ((Math.sin(i * 0.013) * 43758.5453) % 1) * 0.4 - 0.2;
    }
    const noise = context.createBufferSource();
    noise.buffer = buffer;
    noise.loop = true;
    filter = context.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 380;
    filter.Q.value = 0.6;
    noiseGain = context.createGain();
    noiseGain.gain.value = 0.04;
    noise.connect(filter).connect(noiseGain).connect(master);
    noise.start();
  }

  async function start() {
    try {
      if (!context) build();
      await context.resume();
      running = context.state === 'running';
      return running;
    } catch {
      running = false;
      return false;
    }
  }

  async function toggle() {
    try {
      if (!context || !running) return start();
      await context.suspend();
      running = false;
      return false;
    } catch {
      return running;
    }
  }

  function tone(frequency, when, duration, volume, type = 'sine') {
    if (!context || !running) return;
    const oscillator = context.createOscillator();
    const envelope = context.createGain();
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(frequency, when);
    oscillator.frequency.exponentialRampToValueAtTime(Math.max(40, frequency * 1.01), when + duration);
    envelope.gain.setValueAtTime(0.0001, when);
    envelope.gain.exponentialRampToValueAtTime(volume, when + 0.03);
    envelope.gain.exponentialRampToValueAtTime(0.0001, when + duration);
    oscillator.connect(envelope).connect(master);
    oscillator.connect(envelope).connect(reverb);
    oscillator.start(when);
    oscillator.stop(when + duration + 0.05);
  }

  function region(index) {
    if (!context || !running || index === lastRegion) return;
    lastRegion = index;
    const now = context.currentTime;
    const chord = REGION_CHORDS[index] || REGION_CHORDS[0];
    drones.forEach((drone, i) => {
      const freq = chord[i] || chord[0];
      drone.oscillator.frequency.exponentialRampToValueAtTime(freq, now + 1.2);
    });
    tone(chord[0] * 2, now, 2.4, 0.06, 'sine');
    tone(chord[1] * 2, now + 0.15, 2.2, 0.045, 'triangle');
  }

  function lock(toneHz = 330) {
    if (!context || !running) return;
    const now = context.currentTime;
    tone(toneHz, now, 0.9, 0.09, 'sine');
    tone(toneHz * 1.5, now + 0.05, 1.1, 0.06, 'triangle');
    tone(toneHz * 2, now + 0.12, 1.4, 0.04, 'sine');
  }

  function setThrust(amount = 0.5) {
    if (!context || !running || !hullGain) return;
    const now = context.currentTime;
    const level = 0.03 + Math.min(1, Math.max(0, amount)) * 0.08;
    hullGain.gain.linearRampToValueAtTime(level, now + 0.12);
    if (hullOsc) hullOsc.frequency.linearRampToValueAtTime(36 + amount * 28, now + 0.15);
    if (filter) filter.frequency.linearRampToValueAtTime(280 + amount * 420, now + 0.2);
  }

  function arrival(wakeCount = 0) {
    if (!context || !running) return;
    const now = context.currentTime;
    tone(196, now, 3.2, 0.07, 'sine');
    tone(294, now + 0.2, 3.4, 0.055, 'triangle');
    tone(392, now + 0.4, 3.6, 0.04, 'sine');
    if (wakeCount >= 3) tone(523.25, now + 0.6, 4, 0.035, 'sine');
  }

  return {
    start,
    toggle,
    region,
    lock,
    setThrust,
    arrival,
    get running() {
      return running;
    },
  };
}
